import { useEffect, useState } from 'react';
import { createLogger } from '../../../infra/logger';

const log = createLogger('@breath-lab/landing');
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { getServices } from '@breath-lab/services';
import { PublicBreathwork, TrainingBenefit } from '../../../domain/models';
import { breathworkDetailPath, ROUTES } from '../../../routes';
import { breathworkNoBgImageUrl, breathworkNoBgImageSrcSet } from '../../../domain/breathworkHelpers';
import { chooseBreathworkCardChips } from '../../../domain/logic';
import { cardChipLabel } from '../../../domain/breathworkDisplayHelpers';
import { getPrimaryName, getSecondaryName } from '../../../domain/breathworkNameDisplay';
import { useBreathworkNameDisplay } from '../../hooks/useBreathworkNameDisplay';
import Button from '@ui/components/Button/Button';
import MediaCard from '@ui/components/MediaCard/MediaCard';
import { logEvent } from '../../../infra/firebase/analytics';
import { AnalyticsEvent, AnalyticsParam } from '../../../domain/analytics';
import styles from './BreathworkPreviewSection.module.css';

const CURATED_IDS = ['SAMA_VRITTI', 'UJJAYI', 'BHRAMARI'];

const CURATED_STATIC_LINKS = [
  { id: 'SAMA_VRITTI', label: 'Sama Vritti' },
  { id: 'UJJAYI', label: 'Ujjayi' },
  { id: 'BHRAMARI', label: 'Bhramari' },
];

export default function BreathworkPreviewSection() {
  const { t } = useTranslation('landing');
  const [breathworks, setBreathworks] = useState<PublicBreathwork[]>([]);
  const [benefits, setBenefits] = useState<TrainingBenefit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const display = useBreathworkNameDisplay();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);

    async function load() {
      const data = getServices().data;
      const [all, fetchedBenefits] = await Promise.all([
        data.fetchPublicBreathworks(),
        data.fetchBenefits().catch((err) => { log.error('Failed to fetch benefits', err); return []; }),
      ]);

      if (cancelled) return;

      setBenefits(fetchedBenefits);
      const curated = CURATED_IDS
        .map((id) => all.find((b) => b.id === id))
        .filter((b): b is PublicBreathwork => b !== undefined);
      setBreathworks(curated);
      setLoading(false);
    }

    load().catch((err) => {
      log.error('Failed to load breathwork preview', err);
      if (!cancelled) { setLoading(false); setError(true); }
    });
    return () => { cancelled = true; };
  }, [retryKey]);

  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        <div className={styles.header}>
          <h2 className={styles.heading}>{t('breathworkPreview.heading')}</h2>
          <Button variant="ghost" as={Link} to={ROUTES.breathworks} onClick={() => logEvent(AnalyticsEvent.landingBreathworkSeeAllClick)}>
            {t('breathworkPreview.seeAll')}
          </Button>
        </div>
        <p className={styles.intro}>
          {t('breathworkPreview.intro')}
        </p>

        {loading ? (
          <>
            <div className={styles.grid}>
              {Array.from({ length: CURATED_IDS.length }).map((_, i) => (
                <div key={i} className={styles.skeleton} />
              ))}
            </div>
            <nav className={styles.staticLinks} aria-label="Featured breathworks">
              {CURATED_STATIC_LINKS.map((b, i) => (
                <span key={b.id}>
                  {i > 0 && <span aria-hidden="true"> · </span>}
                  <Link to={breathworkDetailPath(b.id)}>{b.label}</Link>
                </span>
              ))}
            </nav>
          </>
        ) : error ? (
          <div className={styles.errorState}>
            <p>{t('breathworkPreview.errorMessage')}</p>
            <Button variant="ghost" onClick={() => setRetryKey((k) => k + 1)}>
              {t('breathworkPreview.retry')}
            </Button>
          </div>
        ) : (
          <ul className={styles.grid} role="list">
            {breathworks.map((b) => {
              const { chips, hasMore } = chooseBreathworkCardChips(b, benefits);
              const tags = [
                ...chips.map((c) => { const l = cardChipLabel(c); return c.emoji ? `${c.emoji} ${l}` : l; }),
                ...(hasMore ? ['+ more'] : []),
              ];
              return (
                <li key={b.id} onClick={() => logEvent(AnalyticsEvent.landingBreathworkCardClick, { [AnalyticsParam.breathworkId]: b.id })}>
                  <MediaCard
                    image={breathworkNoBgImageUrl(b.id) ?? undefined}
                    imageSrcSet={breathworkNoBgImageSrcSet(b.id) ?? undefined}
                    imageAlt={b.name.en}
                    imageContain
                    flairColor={b.backgroundColor}
                    title={getPrimaryName(b.name, display)}
                    subtitle={getSecondaryName(b.name, display)}
                    description={b.goal.en ?? undefined}
                    tags={tags}
                    href={breathworkDetailPath(b.id)}
                  />
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
